import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import SourceViewModal from "./SourceViewModal";
import DateCalendar from "./DateCalendar";
import Tooltip from "./Tooltip";
import { AddTaskIconSvg } from "./Svgs";
import api from "../../../chat/api";
import TaskPic from "../TaskPic.png";

// --- Inline icons (self-contained so the tab has no external icon deps) ---
const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="12" fill="#8B55DD" fillOpacity="0.22" />
    <mask id="brt_mask" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
      <rect width="24" height="24" fill="#D9D9D9" />
    </mask>
    <g mask="url(#brt_mask)">
      <path
        d="M9.9998 13.6L15.8998 7.69999C16.0831 7.51665 16.3165 7.42499 16.5998 7.42499C16.8831 7.42499 17.1165 7.51665 17.2998 7.69999C17.4831 7.88332 17.5748 8.11665 17.5748 8.39999C17.5748 8.68332 17.4831 8.91665 17.2998 9.09999L10.6998 15.7C10.4998 15.9 10.2665 16 9.9998 16C9.73314 16 9.4998 15.9 9.2998 15.7L6.6998 13.1C6.51647 12.9167 6.4248 12.6833 6.4248 12.4C6.4248 12.1167 6.51647 11.8833 6.6998 11.7C6.88314 11.5167 7.11647 11.425 7.3998 11.425C7.68314 11.425 7.91647 11.5167 8.0998 11.7L9.9998 13.6Z"
        fill="#8B55DD"
      />
    </g>
  </svg>
);

const SparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
    <path
      d="M16.625 0.875L15.5225 3.28125L13.125 4.375L15.5225 5.4775L16.625 7.875L17.7188 5.4775L20.125 4.375L17.7188 3.28125M7.875 3.5L5.6875 8.3125L0.875 10.5L5.6875 12.6875L7.875 17.5L10.0625 12.6875L14.875 10.5L10.0625 8.3125M16.625 13.125L15.5225 15.5225L13.125 16.625L15.5225 17.7188L16.625 20.125L17.7188 17.7188L20.125 16.625L17.7188 15.5225"
      fill="url(#brt_sparkle)"
    />
    <defs>
      <linearGradient id="brt_sparkle" x1="10.5" y1="0.875" x2="10.5" y2="20.125" gradientUnits="userSpaceOnUse">
        <stop stopColor="white" />
        <stop offset="1" stopColor="#CCD1FF" />
      </linearGradient>
    </defs>
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 4h6v6M20 4l-9 9M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Date helpers -> "May 01' 2026" ---
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];

const parseDate = (s) => {
  if (!s || typeof s !== "string") return null;
  const v = s.trim();
  let m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // DD/MM/YYYY
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/); // YYYY-MM-DD
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = v.match(/^(\d{1,2})\/(\d{4})$/); // MM/YYYY
  if (m) return new Date(+m[2], +m[1] - 1, 1);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const formatUIDate = (s) => {
  const d = parseDate(s);
  if (!d) return s || "";
  return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}' ${d.getFullYear()}`;
};

// Backend stores extracted dates as DD/MM/YYYY — match that when saving.
const formatToDDMMYYYY = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const ViewSource = ({ disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex items-center gap-1 text-[13px] font-medium ${
      disabled ? "text-[#A04A97] opacity-40 cursor-not-allowed" : "text-[#A04A97] hover:underline"
    }`}
  >
    <ExternalLinkIcon />
    View Source
  </button>
);

// --- Faint, blurred placeholder rendered behind the analysis card (idle state) ---
const BackgroundPreview = () => (
  <div
    aria-hidden="true"
    className="absolute inset-0 px-8 py-6 overflow-hidden select-none pointer-events-none blur-[2px] opacity-60"
  >
    <div className="flex justify-between gap-8">
      <div className="flex-1">
        <p className="text-[11px] font-semibold tracking-wide text-gray-400 mb-2">PROJECT OVERVIEW</p>
        <div className="space-y-2">
          <div className="h-3 bg-gray-200 rounded w-[92%]" />
          <div className="h-3 bg-gray-200 rounded w-[88%]" />
          <div className="h-3 bg-gray-200 rounded w-[80%]" />
          <div className="h-3 bg-gray-200 rounded w-[60%]" />
        </div>
      </div>
      <div className="flex gap-12 shrink-0">
        <div>
          <p className="text-[11px] font-semibold tracking-wide text-gray-400 mb-2">CLIENT</p>
          <div className="h-3 bg-gray-200 rounded w-[120px] mb-4" />
          <p className="text-[11px] font-semibold tracking-wide text-gray-400 mb-2">STATUS</p>
          <div className="h-5 bg-purple-100 rounded w-[80px]" />
        </div>
        <div>
          <p className="text-[11px] font-semibold tracking-wide text-gray-400 mb-2">SUBMISSION DEADLINE</p>
          <div className="h-3 bg-gray-200 rounded w-[100px]" />
        </div>
      </div>
    </div>
    <p className="text-[15px] font-semibold text-gray-500 mt-8 mb-4">Key Dates &amp; Timeline</p>
    <div className="flex gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex-1 border border-gray-200 rounded-lg p-3 bg-[#F8F9FA]">
          <div className="h-3 bg-gray-200 rounded w-3/4 mb-3" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
        </div>
      ))}
    </div>
  </div>
);

const CHECKLIST = ["Project Overview", "Key Dates & Timelines", "Tasks"];

// Fixed Key Dates rows — always rendered. A backend date is mapped in when its
// title matches; otherwise the row shows an empty value.
const FIXED_KEY_DATES = [
  { label: "RFP Issue Date", deadline_type: "rfp_issue", match: /rfp.*issue|issue.*date/i },
  { label: "Bid-Submission Date", deadline_type: "rfp", match: /bid.?submission|bid.*due|submission.*date/i },
  { label: "Questionnaire Submission Date", deadline_type: "questionnaire", match: /questionnaire/i },
  { label: "Contract Start Date", deadline_type: "contract_start", match: /contract.*start|start.*date/i },
  { label: "Contract End Date", deadline_type: "contract_end", match: /contract.*end|end.*date/i },
];

// --- Idle: the "Bid Readiness Analysis" prompt card over the blurred preview ---
const IdleScreen = ({ onRun, isAnalyzing }) => (
  <div className="relative flex-1 h-full w-full overflow-hidden font-outfit bg-[#fafafa]">
    <BackgroundPreview />
    <div aria-hidden="true" className="absolute inset-0 bg-black opacity-[0.26]" />
    <div className="absolute inset-0 flex items-center justify-center px-4">
      <div className="relative w-full max-w-[350px] bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-100 px-4 py-6">
        <h2 className="text-center font-outfit text-[16px] font-semibold leading-[normal] text-[#000000B2]">
          Bid Readiness Analysis
        </h2>
        <p className="mt-2 text-center font-outfit text-[13px] font-normal leading-[normal] text-[#767676]">
          Analyze project requirements, deadlines, and opportunity signals to assess bid readiness,
          identify gaps, and determine whether to move forward with confidence.
        </p>
        <div className="border-t border-[#E8E8E8] my-5" />
        <div className="flex flex-col gap-4">
          {CHECKLIST.map((label) => (
            <div key={label} className="flex items-center gap-3">
              <CheckCircleIcon />
              <span className="font-outfit text-[14px] font-normal leading-[normal] text-[#52525B]">
                {label}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-7">
          <button
            type="button"
            onClick={onRun}
            disabled={isAnalyzing}
            className="inline-flex items-center gap-2 text-white text-[15px] font-medium px-5 py-2.5 transition-opacity hover:opacity-95 disabled:opacity-60 rounded-[6px] bg-[linear-gradient(270deg,rgba(99,102,241,0.94)_0%,rgba(139,92,246,0.94)_100%)] shadow-[0_1px_2px_0_rgba(0,0,0,0.30),0_1px_3px_1px_rgba(0,0,0,0.15)]"
          >
            <SparkleIcon />
            {isAnalyzing ? "Analyzing..." : "Run Analysis"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// --- Processing / loading: spinner over the blurred preview ---
const ProcessingScreen = ({
  title = "Analyzing your documents",
  subtitle = "Extracting key dates and timelines. This may take a moment.",
  // Only true while an analysis is actually running — not when we're merely
  // loading already-finished results, where "you can leave" would be misleading.
  showLeaveHint = true,
}) => (
  <div className="relative flex-1 h-full w-full overflow-hidden font-outfit bg-[#fafafa]">
    <BackgroundPreview />
    <div aria-hidden="true" className="absolute inset-0 bg-black opacity-[0.26]" />
    <div className="absolute inset-0 flex items-center justify-center px-4">
      <div className="relative w-full max-w-[350px] bg-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-gray-100 px-6 py-8 text-center">
        <div className="mx-auto mb-4 w-10 h-10 rounded-full border-[3px] border-[#E9D5FF] border-t-[#8B5CF6] animate-spin" />
        <h2 className="font-outfit text-[16px] font-semibold text-[#000000B2]">{title}</h2>
        <p className="mt-2 font-outfit text-[13px] text-[#767676]">{subtitle}</p>
        {showLeaveHint && (
          <p className="mt-3 font-outfit text-[12px] text-[#9A9A9A]">
            You can leave this page, we'll keep working and notify you when it's ready.
          </p>
        )}
      </div>
    </div>
  </div>
);

const BidReadinessTab = () => {
  const {
    files,
    projectName,
    analysis_summary,
    rfp_status,
    additional_tasks,
    runBidReadinessAnalysis,
    isAnalyzing,
    analysisReady,
    loading,
    projectId,
    fetchFiles,
    showToast,
  } = useOutletContext();

  const docs = useMemo(
    () => (Array.isArray(files) ? files : files?.documents || []),
    [files]
  );

  // Signal-bearing outcomes from OTHER projects. The filtering is server-side —
  // this component receives only what it is allowed to show, so a future screen
  // built against the same endpoint inherits the rule rather than re-deriving it.
  const [priorOutcomes, setPriorOutcomes] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(
          `/api/v1/rfp/folders/${projectId}/bid-outcomes/recall/`
        );
        if (alive) setPriorOutcomes(res?.data?.outcomes || []);
      } catch {
        /* advisory panel — never let it break the readiness screen */
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId]);

  const { dates, tasks } = useMemo(() => {
    const _dates = [];
    const _tasks = [];
    docs.forEach((f) => {
      const common = { document_url: f.url, document_name: f.name, doc_id: f.id };
      (f.insights?.dates || []).forEach((d) => _dates.push({ ...d, ...common }));
      (f.insights?.tasks || []).forEach((t) => _tasks.push({ ...t, ...common, source: "ai" }));
    });
    (Array.isArray(additional_tasks) ? additional_tasks : []).forEach((t) =>
      _tasks.push({ ...t, source: "manual" })
    );
    // Newest tasks first; entries without a timestamp sink to the bottom.
    _tasks.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return { dates: _dates, tasks: _tasks };
  }, [docs, additional_tasks]);

  // Map the extracted dates onto the fixed Key Dates rows (empty when no match).
  const keyDates = useMemo(
    () =>
      FIXED_KEY_DATES.map((row) => {
        const matches = dates.filter((d) => row.match.test(d.date_title || ""));
        const latest = matches.reduce((best, d) => {
          const t = parseDate(d.date)?.getTime();
          if (t == null || isNaN(t)) return best;
          const bt = best ? parseDate(best.date)?.getTime() : null;
          return bt == null || isNaN(bt) || t > bt ? d : best;
        }, null);
        return {
          label: row.label,
          deadline_type: row.deadline_type,
          match: latest || matches[0] || null,
        };
      }),
    [dates]
  );

  // Submission deadline for the overview card reuses the Bid-Submission row.
  const submissionDeadline = useMemo(() => {
    const row = keyDates.find((r) => r.label === "Bid-Submission Date");
    return row?.match ? formatUIDate(row.match.date) : "—";
  }, [keyDates]);

  // --- Source View modal (shared with the existing components) ---
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [sourceModalData, setSourceModalData] = useState(null);

  // --- Add Task (inline expanding form — exact flow from the previous version) ---
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [newTaskInput, setNewTaskInput] = useState({ title: "", description: "" });
  const addTaskRef = useRef(null);

  const handleCreateTask = async () => {
    const { title, description } = newTaskInput;
    if (!title.trim() || !description.trim()) return;

    setIsCreatingTask(true);
    try {
      const response = await api.post(`api/v1/rfp/insights/tasks/add/`, {
        folder_id: parseInt(projectId, 10),
        heading: title.trim(),
        explanation: description.trim(),
      });
      if (response.status === 200 || response.status === 201) {
        if (fetchFiles) await fetchFiles(true);
        showToast?.("success", "Task created successfully!");
        setIsAddingTask(false);
        setNewTaskInput({ title: "", description: "" });
      }
    } catch (error) {
      showToast?.(
        "error",
        error.response?.data?.detail || error.response?.data?.message || "Failed to create task."
      );
    } finally {
      setIsCreatingTask(false);
    }
  };

  // Outside-click saves the form (when both fields are filled) or cancels it —
  // the same behavior as the previous version.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isAddingTask && addTaskRef.current && !addTaskRef.current.contains(event.target)) {
        if (isCreatingTask) return;
        const { title, description } = newTaskInput;
        if (title.trim() && description.trim()) {
          handleCreateTask();
        } else {
          setIsAddingTask(false);
          setNewTaskInput({ title: "", description: "" });
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingTask, newTaskInput, isCreatingTask, projectId]);

  const handleViewSource = useCallback((item) => {
    if (!item?.document_url) return;
    setSourceModalData({
      document_url: item.document_url,
      document_name: item.document_name,
      page_number: item.page_number,
      source_text: item.source_text || item.explanation || item.text,
    });
    setIsSourceModalOpen(true);
  }, []);

  // --- Key Dates: edit via the shared DateCalendar ---
  const [activeDateRow, setActiveDateRow] = useState(null);
  const [calendarPos, setCalendarPos] = useState({ top: 0, left: 0 });
  const [calendarRange, setCalendarRange] = useState(null);
  const calendarRef = useRef(null);

  const openCalendar = useCallback((row, el) => {
    if (!row) return; // opens for existing dates (edit) OR empty rows (add)
    if (el) {
      const rect = el.getBoundingClientRect();
      // DateCalendar is position:fixed → viewport coords. Open just below the
      // trigger; if it would overflow the bottom, clamp it up just enough to
      // stay fully on screen (instead of flipping it to the top of the page).
      const CAL_WIDTH = 270;
      const CAL_HEIGHT = 430;
      const MARGIN = 8;
      let top = rect.bottom + 6;
      if (top + CAL_HEIGHT > window.innerHeight - MARGIN) {
        top = Math.max(MARGIN, window.innerHeight - CAL_HEIGHT - MARGIN);
      }
      // Right-align the calendar to the trigger, clamped to the viewport.
      let left = rect.right - CAL_WIDTH;
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - CAL_WIDTH - MARGIN));
      setCalendarPos({ top, left });
    }
    const parsed = (row.match && parseDate(row.match.date)) || new Date();
    setCalendarRange({ start: parsed, end: parsed });
    setActiveDateRow(row);
  }, []);

  const handleSaveDate = useCallback(
    async (range) => {
      const chosen = range?.start;
      const row = activeDateRow;
      if (!row || !chosen) return false;
      const m = row.match;

      try {
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
        // Existing date → edit it. Empty row → add a manual date (no source doc).
        const isEdit = !!(m?.id && m?.doc_id);
        const res = isEdit
          ? await api.post(
              `/api/v1/rfp/insights/edit/`,
              { folder_id: parseInt(projectId, 10), doc_id: m.doc_id, id: m.id, date: formatToDDMMYYYY(chosen) },
              { headers }
            )
          : await api.post(
              `/api/v1/rfp/insights/date/add/`,
              {
                folder_id: parseInt(projectId, 10),
                date_title: row.label,
                deadline_type: row.deadline_type,
                date: formatToDDMMYYYY(chosen),
              },
              { headers }
            );
        if (res.status === 200 || res.status === 201) {
          if (fetchFiles) await fetchFiles(true);
          return true;
        }
        return false;
      } catch (error) {
        showToast?.(
          "error",
          error.response?.data?.error || error.response?.data?.message || "Failed to update date."
        );
        return false;
      }
    },
    [activeDateRow, projectId, fetchFiles, showToast]
  );

  // Close the calendar when clicking outside of it.
  useEffect(() => {
    if (activeDateRow === null) return;
    const handler = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setActiveDateRow(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [activeDateRow]);

  // Arriving with the analysis already marked complete (e.g. via the completion
  // toast) but files not loaded yet: show the loader rather than an empty
  // results view — otherwise the page looks blank until a manual refresh.
  if (analysisReady && docs.length === 0 && loading) {
    return <ProcessingScreen title="Loading analysis" subtitle="Fetching your project insights…" />;
  }

  if (!analysisReady) {
    if (isAnalyzing) return <ProcessingScreen />;
    if (loading && docs.length === 0) {
      return <ProcessingScreen title="Loading analysis" subtitle="Fetching your project insights…" showLeaveHint={false} />;
    }
    return <IdleScreen onRun={runBidReadinessAnalysis} isAnalyzing={isAnalyzing} />;
  }

  // --- Results ---
  return (
    <div
      className="flex-1 h-full w-full overflow-hidden font-outfit bg-[#FAF9FB] px-6 py-4 flex flex-col"
      onScroll={() => activeDateRow !== null && setActiveDateRow(null)}
    >
      {/* Extraction still running — results fill in as documents finish. */}
      {isAnalyzing && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-[#F3E8FF] border border-[#E9D5FF]">
          <div className="w-4 h-4 rounded-full border-2 border-[#E9D5FF] border-t-[#8B5CF6] animate-spin shrink-0" />
          <span className="text-[13px] font-medium text-[#7C3AED]">
            Analysis in progress — more results may appear as documents finish…
          </span>
        </div>
      )}

      {/* Project Overview card */}
      <div className="bg-[#FFFFFF] border border-[#ECE9EE] rounded-xl px-4 py-4 flex-shrink-0">
        <div className="flex justify-between gap-8 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <p className="text-[13px] font-semibold tracking-wide text-[#767676] mb-2">PROJECT OVERVIEW</p>
            <p className="text-[13px] text-[#000000B2] leading-relaxed w-full font-normal">
              {analysis_summary || "No overview available yet."}
            </p>
          </div>
          <div className="flex flex-col shrink-0 min-w-[180px]">
            <p className="text-[13px] font-semibold tracking-wide text-[#767676] mb-2">CLIENT</p>
            <p className="text-[13px] font-medium text-[#000000B2] mb-4">{projectName || "—"}</p>
            <p className="text-[11px] font-semibold tracking-wide text-[#767676] mb-2">STATUS</p>
            <span className="inline-block self-start bg-[#F7EBF5] text-[#A04A97] text-[13px] font-medium px-3 py-1 rounded mb-4">
              {rfp_status || "—"}
            </span>
            <p className="text-[13px] font-semibold tracking-wide text-[#767676] mb-2">SUBMISSION DEADLINE</p>
            <p className="text-[13px] font-medium text-[#A04A97]">{submissionDeadline}</p>
          </div>
        </div>
      </div>

      {/* What earlier bids taught us. Only reasons that say something about a
          FUTURE bid appear — the server omits price and incumbency, because a
          bid lost on commercials tells you nothing about whether this one is
          winnable, and warning a team off winnable work is the failure this
          panel must not cause. Hidden entirely when there is nothing to say. */}
      {priorOutcomes.length > 0 && (
        <>
          <div className="border-t border-[#EFEDF1] mt-5 mb-4 flex-shrink-0" />
          <div className="flex-shrink-0">
            <h3 className="text-[16px] font-medium text-[#1A1A1A] mb-1">
              From earlier bids
            </h3>
            <p className="text-[12px] text-[#77707E] mb-3">
              Recorded when past projects closed, and what those bids asked for
              at the time. Advisory — they do not change the readiness score.
            </p>
            <div className="space-y-1.5">
              {priorOutcomes.map((o) => (
                <div
                  key={o.project_id}
                  className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${
                    !o.shared_aspects?.length
                      ? "border-[#18101C]/[0.10] bg-[#18101C]/[0.015]"
                      : o.outcome === "Win"
                      ? "border-[#166534]/30 bg-[#166534]/[0.04]"
                      : "border-[#92620B]/30 bg-[#92620B]/[0.04]"
                  }`}
                >
                  {/* A win reaches this panel as precedent, not as a caution.
                      Giving it the same warning triangle as a loss would read as
                      one at a glance, which is the opposite of what it says. */}
                  <span
                    className={`mt-[3px] flex-none ${
                      o.outcome === "Win" ? "text-[#166534]" : "text-[#92620B]"
                    }`}
                    aria-hidden="true"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      {o.outcome === "Win" ? (
                        <path d="M20 6 9 17l-5-5" />
                      ) : (
                        <>
                          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                          <path d="M12 9v4M12 17h.01" />
                        </>
                      )}
                    </svg>
                  </span>
                  <span className="min-w-0 flex-1">
                    {/* A bid can close for several reasons; the server sends only
                        the ones that carry forward, so joining them here cannot
                        leak a price loss into a warning. */}
                    <span className="block text-[13px] text-[#3F3F46]">
                      {o.outcome === "Win"
                        ? "We won a bid asking for the same things"
                        : (o.reason_labels || []).join(" · ")}
                    </span>
                    <span className="block text-[11.5px] text-[#77707E]">
                      {o.outcome} · {o.project_name || `project ${o.project_id}`}
                      {o.closed_at ? ` · ${formatUIDate(o.closed_at.slice(0, 10))}` : ""}
                    </span>
                    {/* The capability areas that bid asked for and we could not
                        evidence at the time. Frozen when the project closed, so
                        it still describes the bid we lost rather than the KB we
                        have since grown. */}
                    {o.gaps?.length > 0 && (
                      <span className="mt-0.5 block text-[11.5px] text-[#77707E]">
                        {o.outcome === "Win" ? "Thin on" : "Could not evidence"}:{" "}
                        {o.gaps.join(", ")}
                      </span>
                    )}
                    {/* Only set when THIS project's own gaps name the same areas.
                        Template rubric names are excluded server-side, so this
                        cannot fire just because both bids scored "Experience Fit". */}
                    {o.shared_aspects?.length > 0 && (
                      <span className="mt-1 block text-[11.5px] font-medium text-[#92620B]">
                        Same gap here: {o.shared_aspects.join(", ")}
                      </span>
                    )}
                    {o.note && (
                      <span className="mt-0.5 block text-[11.5px] italic text-[#77707E]">
                        “{o.note}”
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="border-t border-[#EFEDF1] mt-5 mb-4 flex-shrink-0" />

      {/* Key Dates + Tasks (two columns) — fills the height freed by removing
          Scope of Work; each column scrolls internally instead of the page. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 flex-1 min-h-0">
        {/* Key Dates & Timeline — fixed rows, mapped from backend when available */}
        <div className="flex flex-col min-h-0">
        <div className="flex gap-2 items-center">
            <h3 className="text-[16px] font-medium text-[#1A1A1A] mb-3">Key Dates &amp; Timeline</h3>
         <Tooltip
           placement="right"
           content={
             <div className="whitespace-pre-line z-50">
               {"AI-extracted dates from your RFP documents.\nReview and edit before relying on them."}
             </div>
           }
         >
         <span className="pb-3 cursor-default">
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M6.06667 6C6.2325 5.56776 6.54316 5.20643 6.94565 4.97766C7.34814 4.74888 7.81753 4.66683 8.27377 4.74549C8.73 4.82415 9.14481 5.05866 9.44745 5.409C9.7501 5.75935 9.92182 6.20385 9.93333 6.66667C9.93333 8 7.93333 8.66667 7.93333 8.66667M8 11.3333H8.00667M8 14C8.78793 14 9.56815 13.8448 10.2961 13.5433C11.0241 13.2417 11.6855 12.7998 12.2426 12.2426C12.7998 11.6855 13.2417 11.0241 13.5433 10.2961C13.8448 9.56815 14 8.78793 14 8C14 7.21207 13.8448 6.43185 13.5433 5.7039C13.2417 4.97595 12.7998 4.31451 12.2426 3.75736C11.6855 3.20021 11.0241 2.75825 10.2961 2.45672C9.56815 2.15519 8.78793 2 8 2C6.4087 2 4.88258 2.63214 3.75736 3.75736C2.63214 4.88258 2 6.4087 2 8C2 9.5913 2.63214 11.1174 3.75736 12.2426C4.88258 13.3679 6.4087 14 8 14Z" stroke="#909090" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
         </span>
         </Tooltip>
        </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar divide-y divide-gray-100 border rounded-[14px] bg-white shadow-[0_1px_2px_0_rgba(26,10,30,0.04),0_10px_20px_-22px_rgba(42,10,34,0.22)]">
            {keyDates.map((row) => {
              // AI-extracted dates render a "View Source" line (two lines tall);
              // empty + manual rows are single-line, so pad them more to match.
              const hasViewSource = row.match && !row.match.is_manual;
              return (
              <div
                key={row.label}
                className={`px-4 ${hasViewSource ? "py-2" : "py-4"} flex justify-between items-start gap-3`}
              >
                <div>
                  <p className="text-[14px] font-medium text-[#000000B2]">{row.label}</p>
                  {/* View Source only for AI-extracted dates — manual dates have no source doc. */}
                  {hasViewSource && (
                    <ViewSource
                      disabled={!row.match.document_url}
                      onClick={() => handleViewSource(row.match)}
                    />
                  )}
                </div>
                {row.match ? (
                  <button
                    type="button"
                    onClick={(e) => openCalendar(row, e.currentTarget)}
                    className="font-outfit text-[14px] font-medium leading-[19px] whitespace-nowrap mt-0.5 text-black/70 underline decoration-solid [text-decoration-skip-ink:none] [text-underline-position:from-font] hover:text-[#BB42AE] cursor-pointer"
                  >
                    {formatUIDate(row.match.date)}
                  </button>
                ) : (
                  <button
                    type="button"
                    title="Add date"
                    aria-label={`Add ${row.label}`}
                    onClick={(e) => openCalendar(row, e.currentTarget)}
                    className="mt-0.5 text-gray-400 hover:text-[#BB42AE] cursor-pointer"
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>

        {/* Tasks — height matched to the Key Dates container, scrolls internally */}
        <div className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-2 items-center">
              <h3 className="text-[16px]  font-medium text-[#1A1A1A] ">Tasks</h3>
            <Tooltip
              placement="right"
              content={
                <div className="whitespace-pre-line z-50">
                  {"AI-extracted & manually added tasks from your requirements.\nReview and edit before relying on them."}
                </div>
              }
            >
            <span className="cursor-default">
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M6.06667 6C6.2325 5.56776 6.54316 5.20643 6.94565 4.97766C7.34814 4.74888 7.81753 4.66683 8.27377 4.74549C8.73 4.82415 9.14481 5.05866 9.44745 5.409C9.7501 5.75935 9.92182 6.20385 9.93333 6.66667C9.93333 8 7.93333 8.66667 7.93333 8.66667M8 11.3333H8.00667M8 14C8.78793 14 9.56815 13.8448 10.2961 13.5433C11.0241 13.2417 11.6855 12.7998 12.2426 12.2426C12.7998 11.6855 13.2417 11.0241 13.5433 10.2961C13.8448 9.56815 14 8.78793 14 8C14 7.21207 13.8448 6.43185 13.5433 5.7039C13.2417 4.97595 12.7998 4.31451 12.2426 3.75736C11.6855 3.20021 11.0241 2.75825 10.2961 2.45672C9.56815 2.15519 8.78793 2 8 2C6.4087 2 4.88258 2.63214 3.75736 3.75736C2.63214 4.88258 2 6.4087 2 8C2 9.5913 2.63214 11.1174 3.75736 12.2426C4.88258 13.3679 6.4087 14 8 14Z" stroke="#909090" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
         </span>
            </Tooltip>
            </div>

            <button
              type="button"
              onClick={() => setIsAddingTask((v) => !v)}
              className="text-xs font-semibold text-[#2563EB] flex items-center gap-1 font-outfit"
            >
              <AddTaskIconSvg /> Add Task
            </button>
          </div>
          <div className="relative flex-1 min-h-[260px] lg:min-h-0">
            <div className=" absolute inset-0 divide-y divide-[#ECE9EE] border rounded-[14px] bg-white shadow-[0_1px_2px_0_rgba(26,10,30,0.04),0_10px_20px_-22px_rgba(42,10,34,0.22)] overflow-y-auto custom-scrollbar">
            {isAddingTask && (
              <div className="p-2" ref={addTaskRef}>
                {isCreatingTask ? (
                  <div className="flex items-center justify-center py-3">
                    <p className="text-[13px] text-gray-600 font-outfit">Creating Task...</p>
                  </div>
                ) : (
                  <div className="bg-[#F5F5F5] rounded-lg p-2">
                    <input
                      type="text"
                      placeholder="Title"
                      autoFocus
                      value={newTaskInput.title}
                      onChange={(e) => setNewTaskInput({ ...newTaskInput, title: e.target.value })}
                      style={{ fontSize: "13px", lineHeight: "18px", fontWeight: 400 }}
                      className="w-full h-7 px-2 bg-white border border-gray-200 rounded-md text-[13px] text-[#000000B3] placeholder-gray-400 focus:outline-none shadow-sm font-outfit"
                    />
                    <textarea
                      placeholder="Write your details here"
                      rows={2}
                      value={newTaskInput.description}
                      onChange={(e) => setNewTaskInput({ ...newTaskInput, description: e.target.value })}
                      style={{ fontWeight: 400 }}
                      className="w-full mt-1 bg-transparent border-none p-0 px-1 text-[13px] text-[#00000080] placeholder:text-[13px] placeholder-[#00000080] focus:outline-none resize-none font-outfit"
                    />
                  </div>
                )}
              </div>
            )}
            {tasks.length > 0 ? (
              tasks.map((t, i) => (
                <div key={t.id || i} className="px-4 pt-3 pb-1.5">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[14px] font-medium text-[#000000B2] max-w-[80%]">{t.heading}</p>
                    <span
                      className={`text-[12px] font-medium px-2 py-0.5 rounded ${
                        t.source === "manual"
                          ? "bg-[#F1EBF7] text-[#8B55DD]"
                          : "bg-[#F1EBF7] text-[#8B55DD]"
                      }`}
                    >
                      {t.source === "manual" ? "Manual" : "AI Generated"}
                    </span>
                  </div>
                  <p className="text-[14px] text-[#000000B2] font-normal leading-snug">{t.explanation}</p>
                  {t.document_url && (
                    <div className="mt-1">
                      <ViewSource disabled={!t.document_url} onClick={() => handleViewSource(t)} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="h-full w-full flex flex-col items-center justify-center gap-3 px-4 py-8">
                <img
                  src={TaskPic}
                  alt="No tasks yet"
                  className="w-80 max-w-[90%] h-auto opacity-90 select-none"
                />
                <p className="text-[14px] text-gray-400 italic">No tasks found.</p>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      <SourceViewModal
        isOpen={isSourceModalOpen}
        onClose={() => setIsSourceModalOpen(false)}
        data={sourceModalData}
      />

      <DateCalendar
        isOpen={activeDateRow !== null}
        position={calendarPos}
        onClose={() => setActiveDateRow(null)}
        onSave={handleSaveDate}
        onRefresh={() => {}}
        initialDateRange={calendarRange}
        menuRef={calendarRef}
      />
    </div>
  );
};

export default BidReadinessTab;
